import React from "react";
import { useTranslation } from "react-i18next";
import { deserializeAssetCategorization } from "../../data/asset-categorization";
import { GroupTag } from "../../elements/categorization-tag";
import Divider from "../../elements/divider";
import { VelavuMenuFilterItem } from "../../elements/velavu-menu-filter";
import { classArr } from "../../helper/style-helper";
import * as styles from "./analytics-sidebar-filter.module.scss";

export interface AnalyticsSidebarFilterProps {
	/// Whether this component is expanded, and can show more detailed filters
	expanded: boolean;

	/// All categorizations available for the user to select
	allCategorizations: readonly string[];
	/// The categorizations the user has selected
	selectedCategorizations: readonly string[];
	/// Callback to set the selected categorizations
	onSetSelectedCategorizations: React.Dispatch<
		React.SetStateAction<string[]>
	>;

	/// The status by which to filter, or undefined for no preference
	filterStatus: boolean | undefined;
	/// Callback to set the status by which to filter
	setFilterStatus: (filter: boolean | undefined) => void;
}

/**
 * An expandable component that allows the user to filter
 * devices by status and optionally category
 */
export default function AnalyticsSidebarFilter(
	props: AnalyticsSidebarFilterProps,
) {
	const { t } = useTranslation();

	if (props.allCategorizations.length === 0 && !props.expanded) {
		return null;
	}

	return (
		<>
			<div
				className={classArr(
					styles.container,
					!props.expanded && styles.containerCollapsed,
				)}
			>
				{props.allCategorizations.map((categorization) => (
					<GroupTag
						key={categorization}
						categorization={deserializeAssetCategorization(
							categorization,
						)!}
						selectable
						selected={props.selectedCategorizations.includes(
							categorization,
						)}
						onClick={() =>
							props.onSetSelectedCategorizations?.(
								(categorizations) => {
									const selectionIndex =
										categorizations.indexOf(categorization);
									if (selectionIndex === -1) {
										return categorizations.concat(
											categorization,
										);
									} else {
										const newSelection = [
											...categorizations,
										];
										newSelection.splice(selectionIndex, 1);
										return newSelection;
									}
								},
							)
						}
					/>
				))}

				<div className={styles.optionContainer}>
					<div className={styles.optionColumn}>
						<div className={styles.optionTitle}>
							{t("analytics.status_filter")}
						</div>
						<VelavuMenuFilterItem
							checked={props.filterStatus === undefined}
							onClick={() => props.setFilterStatus(undefined)}
						>
							{t("floor_filter.all")}
						</VelavuMenuFilterItem>
						<VelavuMenuFilterItem
							checked={props.filterStatus === true}
							onClick={() => props.setFilterStatus(true)}
						>
							{t("connection_status.active")}
						</VelavuMenuFilterItem>
						<VelavuMenuFilterItem
							checked={props.filterStatus === false}
							onClick={() => props.setFilterStatus(false)}
						>
							{t("connection_status.inactive")}
						</VelavuMenuFilterItem>
					</div>
				</div>
			</div>
			<Divider />
		</>
	);
}
